import * as React from 'react';
import { Suspense, useState } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import { Typography, useTheme } from '@mui/material';
import localStoreUtil from '../ApiSetUp/localstore.util';
import { useAppSelector } from '../Redux/app/hooks';
import Assets from '../Component/Common/Assets';
import FilePreview from '../Component/Common/FilePreview';
import { getNavLayers } from './nav';
import { drawerWidth, miniDrawerWidth } from './Sidebar';
import { isSuperuser, getUserData } from '../lib/permissions';

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme }) => ({
        flexGrow: 1,
        padding: theme.spacing(1),
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
    }),
);

function OutletFallback() {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240, width: '100%' }}>
            <CircularProgress size={32} sx={{ color: '#E77843' }} />
        </Box>
    );
}

export default function Layout() {
    const theme = useTheme();
    const confirmDialogue = useConfirm();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(true);

    const handleDrawerToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const getPermissionList = useAppSelector((state) => state?.auth?.getRolePermission);

    const userData = getUserData();
    const superuser = isSuperuser(userData);
    const canSection = (section) =>
        superuser ||
        getPermissionList?.editPermission?.includes(section) ||
        getPermissionList?.viewPermission?.includes(section);

    const _logOut = () => {
        confirmDialogue({
            title: (
                <Box>
                    <PowerSettingsNewOutlinedIcon style={{ fontSize: '60px', color: theme.palette.primary.main, }} />
                    <Typography fontSize={"24px"} fontWeight={700}>Are you sure you want to logout?</Typography>
                </Box>
            ),
        })
            .then(() => {
                localStoreUtil.remove_all()
                navigate("/login")
            })
            .catch((err) => {
                console.error(err)
            });
    };

    const sidebarList = [
        {
            name: 'Dashboard',
            path: '/',
            section: 'Overview',
            icon: <Assets absolutePath={true} src={`/assets/img/Dashboard.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActiveDashboard.svg`} />,
            permission: true,
        },
        {
            name: 'Vendor Management',
            path: '/vendor-management',
            section: 'Operations',
            icon: <Assets absolutePath={true} src={`/assets/img/Vendor.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActiveVendor.svg`} />,
            permission: canSection('Vendor Management'),
        },
        {
            name: 'Inventory',
            path: '/inventory',
            section: 'Operations',
            icon: <Assets absolutePath={true} src={`/assets/img/Inventory.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActiveInventory.svg`} />,
            permission: canSection('Inventory Management'),
            subData: [
                {
                    name: 'Common',
                    path: '/inventory/common',
                    subData: [
                        { name: "Unit", path: '/inventory/common/unit' },
                        { name: "Mill Name", path: '/inventory/common/company-name' },
                        { name: "Group", path: '/inventory/common/group' },
                        { name: "GSM", path: '/inventory/common/sub-group' },
                        { name: "Size", path: '/inventory/common/size' },
                        { name: "Color", path: '/inventory/common/color' },
                        { name: "Release Status", path: '/inventory/common/release-status' },
                    ]
                },
                { name: 'Type of Paper', path: '/inventory/type-of-paper' },
                { name: 'Micron', path: '/inventory/micron' },
                { name: 'Chemical Name', path: '/inventory/chemical-name' },
                { name: 'Chemical Category', path: '/inventory/chemical-category' },
                { name: 'Chemical Code', path: '/inventory/chemical-code' },
                { name: 'Ink', path: '/inventory/pantone-number' },
                { name: "Final Product", path: "/inventory/product" }
            ]
        },
        {
            name: 'Purchase Order',
            path: '/purchase-order',
            section: 'Operations',
            icon: <Assets absolutePath={true} src={`/assets/img/PurchaseOrder.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActivePurchaseOrder.svg`} />,
            permission: canSection('Purchase Order'),
            subData: [
                { name: 'Documents', path: '/purchase-order/po-documents' },
            ]
        },
        {
            name: 'Purchase Entry',
            path: '/purchase-entry',
            section: 'Operations',
            icon: <Assets absolutePath={true} src={`/assets/img/PurchaseEntry.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActivePurchaseEntry.svg`} />,
            permission: canSection('Purchase Entry'),
            subData: [
                { name: 'Lot Number List', path: '/purchase-entry/lot-number-list' },
                { name: 'GRN Number', path: '/purchase-entry/grn-number' },
                { name: 'Production', path: '/purchase-entry/production' },
            ]
        },
        {
            name: 'Processing',
            path: '/processing-unit-list',
            section: 'Operations',
            icon: <Assets absolutePath={true} src={`/assets/img/Processing.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActiveProcessing.svg`} />,
            permission: canSection('Processing'),
        },
        {
            name: 'Account',
            path: '/account',
            section: 'Sales',
            icon: <Assets absolutePath={true} src={`/assets/img/Invoice.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActiveInvoice.svg`} />,
            permission: canSection('Account'),
            rootNavName: 'Reports',
            subData: [
                { name: 'AI Insights', path: '/account/insights' },
                { name: 'Customers', path: '/account/customers' },
                { name: 'Items', path: '/account/items' },
                { name: 'Invoice', path: '/account/invoice' },
                { name: 'Settings', path: '/account/settings' },
            ],
        },
        {
            name: 'Documents',
            path: '/documents',
            section: 'Sales',
            icon: <Assets absolutePath={true} src={`/assets/img/Document.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActiveDocument.svg`} />,
            permission: canSection('Documents'),
        },
        {
            name: 'User & Role',
            path: '/user-role',
            section: 'System',
            icon: <Assets absolutePath={true} src={`/assets/img/UserRole.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActiveUserRole.svg`} />,
            permission: superuser,
        },
    ];

    const filteredTabs = sidebarList?.filter((tab) => tab.permission);
    const nav = getNavLayers(filteredTabs, location.pathname);

    return (
        <>
            <Header open={open} logOut={_logOut} filteredTabs={filteredTabs} nav={nav} />
            <Sidebar open={open} handleDrawerToggle={handleDrawerToggle} logOut={_logOut} filteredTabs={filteredTabs} />
            <Main open={open} sx={{ marginTop: `${nav.chromeHeight}px`, marginLeft: open ? `${drawerWidth}px` : `${miniDrawerWidth}px`, padding: { xs: '16px', md: '20px 24px' }, background: '#F7F7F8', minHeight: `calc(100vh - ${nav.chromeHeight}px)`, boxSizing: 'border-box' }}>
                <Suspense fallback={<OutletFallback />}>
                    <Outlet />
                </Suspense>
            </Main>
            <FilePreview />
        </>
    );
}
